import type { ReactNode, SVGProps } from 'react';
import { Label } from '@immo/ui-kit';

type SegmentedFieldOption = {
  readonly value: string;
  readonly label: string;
  readonly icon?: (props: SVGProps<SVGSVGElement>) => ReactNode;
};

type SegmentedFieldProps = {
  readonly id: string;
  readonly label: string;
  readonly required?: boolean;
  readonly error?: string | null;
  readonly value: string;
  readonly options: ReadonlyArray<SegmentedFieldOption>;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
};

export function SegmentedField(props: SegmentedFieldProps) {
  const { id, label, required, error = null, value, options, onChange, disabled = false } = props;
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;
  const ariaDescribedBy = error !== null ? errorId : undefined;

  return (
    <div className="space-y-1.5">
      {/* A native `label[for]` only computes an accessible name for
          "labelable" elements (input, select, ...); this radiogroup is a
          div, so `for` alone leaves it nameless in the real accessibility
          tree even though jsdom-based unit tests don't catch that (IMM-812). */}
      <Label id={labelId}>
        {label}
        {required === true && <span className="ml-0.5 text-danger-600">*</span>}
      </Label>
      <div
        id={id}
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={ariaDescribedBy}
        className="grid grid-flow-col auto-cols-fr gap-2"
      >
        {options.map((opt) => {
          const selected = opt.value === value;
          const Icon = opt.icon;
          const classes = [
            'inline-flex w-full items-center justify-center gap-2 rounded-md border-2 px-3 py-2 text-base-sm shadow-sm transition-colors focus:outline-none focus:shadow-focus disabled:cursor-not-allowed disabled:opacity-50',
            selected
              ? 'border-interactive bg-interactive-subtle-bg text-text-primary font-semibold'
              : 'border-border bg-surface text-text-secondary hover:border-border-strong hover:bg-hover-bg',
          ].join(' ');
          const iconClasses = ['h-4 w-4 shrink-0', selected ? 'text-interactive' : 'text-text-tertiary'].join(' ');

          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(opt.value)}
              disabled={disabled}
              className={classes}
            >
              {Icon !== undefined && <Icon className={iconClasses} aria-hidden />}
              <span className="truncate">{opt.label}</span>
            </button>
          );
        })}
      </div>
      {error !== null && (
        <p id={errorId} role="alert" className="text-sm text-danger-600">
          {error}
        </p>
      )}
    </div>
  );
}
